import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CompanyProductPhaseBoard } from 'src/app/models/company_product_phase_board.model';
import { ApiService } from 'src/app/services/api.service';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PermissionsService } from 'src/app/services/permissions.service';
import { EditCompanyProductPhaseBoardDialogComponent } from '../company-product-phase-boards/company-product-phase-board-detail/edit-company-product-phase-board-dialog/edit-company-product-phase-board-dialog.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { CompanyProductPhaseBoardDetailComponent } from './company-product-phase-board-detail/company-product-phase-board-detail.component';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-company-product-phase-boards',
  templateUrl: './company-product-phase-boards.component.html',
  styleUrls: ['./company-product-phase-boards.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    RouterLink,
    MatIcon,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatCheckbox,
    RouterLinkActive,
    RouterOutlet,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatTooltip,
  ],
})
export class CompanyProductPhaseBoardsComponent implements OnInit {
  protected fb = inject(UntypedFormBuilder);
  protected activatedRoute = inject(ActivatedRoute);
  private api = inject(ApiService);
  protected router = inject(Router);
  private permissions = inject(PermissionsService);
  private dialog = inject(MatDialog);

  private company_product_id: number;
  private phase_id: number;
  company_product: any = null as any;
  boards: CompanyProductPhaseBoard[] = [];
  showColumn: boolean = true;
  readonly can_act_as_product_manager = toSignal(
    (this.activatedRoute.parent ?? this.activatedRoute).paramMap.pipe(
      map(
        (): number =>
          +((this.activatedRoute.snapshot.paramMap.get('company_product_id') ??
            this.activatedRoute.parent?.snapshot.paramMap.get('company_product_id') ??
            this.activatedRoute.parent?.parent?.snapshot.paramMap.get('company_product_id')) as string)
      ),
      filter((id): id is number => Number.isFinite(id) && id > 0),
      distinctUntilChanged(),
      switchMap(id => this.api.getCompanyProductUserPermissions(id)),
      map(permissions => permissions.can_act_as_product_manager)
    ),
    { initialValue: false }
  );

  filterForm: UntypedFormGroup;
  private formPersistence: any;

  @ViewChild(RouterOutlet) outlet?: RouterOutlet;

  constructor() {
    this.filterForm = this.fb.group({
      show_archived_boards: [
        this.activatedRoute.snapshot.queryParamMap.get('show_archived_boards') != null
          ? this.activatedRoute.snapshot.queryParamMap.get('show_archived_boards') == 'true'
          : false,
      ],
      mode: [
        this.activatedRoute.snapshot.queryParamMap.get('mode') != null ? this.activatedRoute.snapshot.queryParamMap.get('mode') : 'all',
      ],
    });
  }

  ngOnInit(): void {
    this.activatedRoute.parent?.paramMap.subscribe((): void => {
      this.initFilterFormListener();
      this.initRouteParameters();
      this.fetchCompanyProduct();
      this.fetchBoards();
    });
  }

  openBoardSettings(board: CompanyProductPhaseBoard) {
    const dialogRef = this.dialog.open(EditCompanyProductPhaseBoardDialogComponent, {
      width: '1000px',
      data: {
        board: board,
        company_product_id: this.company_product_id,
        company_id: this.company_product.company_id,
      },
    });

    dialogRef.afterClosed().subscribe((data: CompanyProductPhaseBoard | undefined) => {
      if (data) {
        this.fetchBoards();
      }
    });
  }

  deleteBoard(board: CompanyProductPhaseBoard) {
    if (!confirm('¿Estás seguro que quieres eliminar este tablero?')) {
      return;
    }

    const deleted_board_id = Number(board.id);
    const is_viewing_deleted_board = this.router.url.includes(`/boards/${deleted_board_id}`);

    this.api.deleteCompanyProductPhaseBoard(deleted_board_id.toString()).subscribe(() => {
      this.boards = this.boards.filter(b => b.id !== board.id);

      if (is_viewing_deleted_board) {
        const target_board = this.boards.find(b => b.status === 'active') ?? this.boards[0];
        if (target_board?.id) {
          this.navigateToBoard(Number(target_board.id));
        }
      }

      this.fetchBoards();
    });
  }

  openCreateBoard() {
    const active_board = this.boards.find(board => board.status === 'active');
    const start_date = active_board?.end_date ? new Date(active_board.end_date) : new Date();
    const end_date = new Date(start_date);
    end_date.setDate(end_date.getDate() + 14);

    const dialogRef = this.dialog.open(EditCompanyProductPhaseBoardDialogComponent, {
      width: '1000px',
      data: {
        board: {
          id: null,
          company_product_phase_id: this.phase_id,
          title: '',
          start_date,
          end_date,
          default_followers: [],
          product_tasks_boards: [],
        },
        company_product_id: this.company_product_id,
        company_id: this.company_product.company_id,
      },
    });

    dialogRef.afterClosed().subscribe((data: CompanyProductPhaseBoard | undefined) => {
      if (data?.id) {
        this.fetchBoards();
        this.navigateToBoard(Number(data.id));
      }
    });
  }

  getDiferenceInDays(endingDate: Date) {
    const days = Math.abs(Math.floor((+new Date() - +new Date(endingDate)) / (1000 * 3600 * 24)));
    return days;
  }

  refetchBoard() {
    const board = this.outlet?.component;
    if (board && board instanceof CompanyProductPhaseBoardDetailComponent) {
      board.fetchBoard();
    }
  }

  private initRouteParameters() {
    this.company_product_id = +((this.activatedRoute.snapshot.paramMap.get('company_product_id') ??
      this.activatedRoute.parent?.snapshot.paramMap.get('company_product_id') ??
      this.activatedRoute.parent?.parent?.snapshot.paramMap.get('company_product_id')) as string);
    this.phase_id = +((this.activatedRoute.snapshot.paramMap.get('phase_id') ??
      this.activatedRoute.parent?.snapshot.paramMap.get('phase_id')) as string);
  }

  private initFilterFormListener() {
    this.filterForm.valueChanges.subscribe(data => {
      if (this.formPersistence == null || JSON.stringify(this.formPersistence) != JSON.stringify(data)) {
        this.router.navigate([], { queryParams: data });
        this.fetchBoards();
      }
    });
  }

  private fetchBoards() {
    let params = null;
    if (!this.filterForm.get('show_archived_boards')?.value) {
      params = { is_archived: false, with: 'default_followers', order_by: 'start_date:desc' };
    }
    this.api.getPhaseBoards(this.phase_id, params).subscribe(boards => {
      this.boards = boards.sort((a, b) => {
        const valA = a.status === 'active' ? 0 : a.status === 'not_started' ? 1 : 2;
        const valB = b.status === 'active' ? 0 : b.status === 'not_started' ? 1 : 2;

        return valA - valB;
      });

      let firstActiveBoard = this.boards.find(board => board.status === 'active');
      if (!firstActiveBoard) {
        firstActiveBoard = this.boards[0];
      }

      const onBoardDetail = this.router.url.includes('/boards/') && /\d+/.test(this.router.url.split('/boards/')[1]);
      if (firstActiveBoard && !onBoardDetail) {
        let rootRoute = '/';
        if (this.permissions.isWorker()) {
          rootRoute = '/employees/development';
        } else if (this.permissions.isClient()) {
          rootRoute = '/clients';
        } else if (this.permissions.isPartner()) {
          rootRoute = '/partners';
        }

        this.router.navigate(
          [rootRoute, 'company-products', this.company_product_id, 'phases', this.phase_id, 'boards', firstActiveBoard.id],
          {
            queryParams: {
              show_archived_boards: this.filterForm.get('show_archived_boards')?.value,
              mode: this.filterForm.get('mode')?.value,
            },
          }
        );
      }
    });
  }

  private fetchCompanyProduct() {
    this.api.getCompanyProduct(this.company_product_id.toString()).subscribe(company_product => {
      this.company_product = company_product;
    });
  }

  private navigateToBoard(board_id: number) {
    let root_route = '/';
    if (this.permissions.isWorker()) {
      root_route = '/employees/development';
    } else if (this.permissions.isClient()) {
      root_route = '/clients';
    } else if (this.permissions.isPartner()) {
      root_route = '/partners';
    }

    this.router.navigate([root_route, 'company-products', this.company_product_id, 'phases', this.phase_id, 'boards', board_id], {
      queryParams: {
        show_archived_boards: this.filterForm.get('show_archived_boards')?.value,
        mode: this.filterForm.get('mode')?.value,
      },
    });
  }
}
